/**
 * Module dependencies.
 */

import {
  DescriptionWrapper,
  ProductsDescription,
  ProductsWrapper,
  Wrapper,
} from './products.style';

import { FadeInUp } from 'components/atoms/in-view-once/fade-in-up';
import { InViewOnce } from 'components/atoms/in-view-once/in-view-once';
import { JSX, useMemo } from 'react';
import { Product } from '../product/product';
import { ProductType } from 'types/product';
import { Title } from 'components/atoms/title/title';
import { getAsset } from 'utils/resolve-admin';
import { useTranslation } from 'next-i18next';

/**
 * `Props` type.
 */

type Props = {
  products: ProductType[];
};

/**
 * Export `Products` component.
 */

export function Products({ products }: Props): JSX.Element {
  const [translate, i18n] = useTranslation();
  const data = useMemo(() => {
    return (products ?? []).map((item) => {
      const lang = i18n.language;

      return {
        description:
          item.description[lang] ??
          item.description[i18n.options.fallbackLng as string],
        src: getAsset(item.image?.filename_disk),
        title:
          item.title[lang] ?? item.title[i18n.options.fallbackLng as string],
      };
    });
  }, [i18n.language, i18n.options.fallbackLng, products]);

  return (
    <Wrapper>
      <Title>{translate('home.products.title')}</Title>

      <DescriptionWrapper>
        <InViewOnce AnimationComponent={FadeInUp}>
          <ProductsDescription>
            {translate('home.products.description')}
          </ProductsDescription>
        </InViewOnce>
      </DescriptionWrapper>

      <ProductsWrapper>
        {data.map((product, index) => (
          <InViewOnce AnimationComponent={FadeInUp} key={index}>
            <Product
              description={product.description}
              imageSrc={product.src}
              title={product.title}
            />
          </InViewOnce>
        ))}
      </ProductsWrapper>
    </Wrapper>
  );
}
